"use client";
import {
  AtSign,
  CreditCard,
  User,
  Database,
  FileSearch,
  Paintbrush,
  Check,
} from "lucide-react";
import type React from "react";
import { Badge } from "@/components/ui/badge";

export default function GridList08() {
  return (
    <section className="pb-20 pt-20 md:pb-32 md:pt-32 container mx-auto max-w-5xl">
      <div className="text-center space-y-4 pb-16 mx-auto max-w-4xl">
        <Badge>Features</Badge>
        <h2 className="mx-auto mt-4 text-3xl font-bold sm:text-5xl tracking-tight">
          Loved by developers
        </h2>
        <p className="text-xl text-muted-foreground pt-1">
          Do not just take our word for it. Here&apos;s what others have to say.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mx-auto">
        <FeatureSection
          icon={<AtSign className="w-8 h-8 text-primary" />}
          title="Emails"
          features={[
            "Send transactional emails",
            "DNS setup to avoid spam folder (DKIM, DMARC, SPF in subdomain)",
            "Webhook to receive & forward emails",
            { text: "Time saved: 3 hours", highlight: true },
            { text: "Headaches: 0", highlight: true },
          ]}
        />
        <FeatureSection
          icon={<CreditCard className="w-8 h-8 text-primary" />}
          title="Payments"
          features={[
            "Process credit card payments",
            "Handle subscriptions and recurring billing",
            "Secure payment gateway integration",
            { text: "Time saved: 5 hours", highlight: true },
            { text: "Revenue potential: Unlimited", highlight: true },
          ]}
        />
        <FeatureSection
          icon={<User className="w-8 h-8 text-primary" />}
          title="Login"
          features={[
            "Secure user authentication",
            "Social login integration",
            "Password reset functionality",
            { text: "Time saved: 4 hours", highlight: true },
            { text: "User satisfaction: 100%", highlight: true },
          ]}
        />
        <FeatureSection
          icon={<Database className="w-8 h-8 text-primary" />}
          title="Database"
          features={[
            "Scalable database setup",
            "Automated backups",
            "Data migration tools",
            { text: "Time saved: 6 hours", highlight: true },
            { text: "Data integrity: Guaranteed", highlight: true },
          ]}
        />
        <FeatureSection
          icon={<FileSearch className="w-8 h-8 text-primary" />}
          title="SEO"
          features={[
            "Optimized meta tags",
            "Sitemap generation",
            "Structured data implementation",
            { text: "Time saved: 3 hours", highlight: true },
            { text: "Potential traffic boost: 200%", highlight: true },
          ]}
        />
        <FeatureSection
          icon={<Paintbrush className="w-8 h-8 text-primary" />}
          title="Style"
          features={[
            "Customizable UI components",
            "Responsive design templates",
            "Dark mode support",
            { text: "Time saved: 8 hours", highlight: true },
            { text: "Design consistency: Perfect", highlight: true },
          ]}
        />
      </div>
    </section>
  );
}

function FeatureSection({
  icon,
  title,
  features,
}: {
  icon: React.ReactNode;
  title: string;
  features: (string | { text: string; highlight: boolean })[];
}) {
  return (
    <div className="bg-accent/20 p-6 backdrop-blur-sm rounded-xl border transition-all duration-300 ease-in-out col-span-full">
      <div className="flex items-center gap-3 mb-4 cursor-pointer">
        <div className="p-2 bg-primary/10 rounded-full">{icon}</div>
        <h2 className="text-2xl font-semibold">{title}</h2>
      </div>
      <div className="space-y-3">
        {features.map((feature, index) => (
          <Feature
            key={index}
            text={typeof feature === "string" ? feature : feature.text}
            highlight={typeof feature === "string" ? false : feature.highlight}
          />
        ))}
      </div>
    </div>
  );
}

function Feature({
  text,
  highlight = false,
}: {
  text: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex mt-5 items-center gap-2 group">
      <Check
        className={`w-5 h-5 ${
          highlight ? "text-primary" : ""
        } transition-transform duration-300 ease-in-out group-hover:scale-110`}
      />
      <span
        className={`${
          highlight ? "text-primary font-medium" : ""
        } group-hover:translate-x-1 transition-transform duration-300 ease-in-out`}
      >
        {text}
      </span>
    </div>
  );
}
